import React from 'react';

/**
 * InboxControlsBar Component
 * Search input, project filter, priority filter, sort ordering, and density / tune toggle.
 */
export default function InboxControlsBar({
    searchQuery,
    onSearchChange,
    selectedProject,
    onProjectChange,
    projects = [],
    selectedPriority,
    onPriorityChange,
    selectedSort,
    onSortChange,
    activeTab,
    isAdmin = false,
    mentionsScope = 'my',
    onMentionsScopeChange,
    loading = false,
    onRefresh,
}) {
    return (
        <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 bg-white dark:bg-[#181818] border-b border-slate-200/50 dark:border-[#303030]">
                <div className="flex flex-wrap items-center gap-3 flex-1 min-w-0">
                    {/* Search Input */}
                    <div className="relative min-w-[240px] md:w-72 flex-shrink-0">
                        <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 text-base">
                            search
                        </span>
                        <input
                            id="timeline-search"
                            className="w-full bg-slate-50 dark:bg-[#141414] border border-slate-200 dark:border-[#303030] pl-9 pr-8 py-1.5 rounded-lg text-xs focus:ring-2 focus:ring-blue-500 transition-all text-slate-700 dark:text-slate-200 dark:placeholder-slate-500 shadow-2xs outline-none"
                            placeholder="Search activity, projects, users..."
                            type="text"
                            value={searchQuery}
                            onChange={(e) => onSearchChange(e.target.value)}
                        />
                        {searchQuery && (
                            <button
                                onClick={() => onSearchChange('')}
                                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                            >
                                <span className="material-symbols-outlined text-sm">cancel</span>
                            </button>
                        )}
                    </div>

                    <div className="h-4 w-px bg-slate-200 dark:bg-[#303030] hidden sm:block" />

                    {/* Project Filter */}
                    <div className="flex items-center gap-1.5 text-xs">
                        <span className="text-slate-400 dark:text-slate-400 font-medium">Project:</span>
                        <select
                            value={selectedProject}
                            onChange={(e) => onProjectChange(e.target.value)}
                            className="bg-slate-50 dark:bg-[#141414] border border-slate-200 dark:border-[#303030] rounded-lg text-xs font-semibold text-slate-700 dark:text-slate-200 py-1.5 pl-2.5 pr-7 focus:ring-2 focus:ring-blue-500 cursor-pointer shadow-2xs outline-none"
                        >
                            <option value="All">All Projects</option>
                            {projects.map((proj) => (
                                <option key={proj} value={proj}>
                                    {proj}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Priority Filter */}
                    <div className="flex items-center gap-1.5 text-xs">
                        <span className="text-slate-400 dark:text-slate-400 font-medium">Priority:</span>
                        <select
                            value={selectedPriority}
                            onChange={(e) => onPriorityChange(e.target.value)}
                            className="bg-slate-50 dark:bg-[#141414] border border-slate-200 dark:border-[#303030] rounded-lg text-xs font-semibold text-slate-700 dark:text-slate-200 py-1.5 pl-2.5 pr-7 focus:ring-2 focus:ring-blue-500 cursor-pointer shadow-2xs outline-none"
                        >
                            <option value="All">All</option>
                            <option value="Critical">Critical</option>
                            <option value="High Priority">High Priority</option>
                            <option value="Medium">Medium</option>
                            <option value="Normal">Normal</option>
                        </select>
                    </div>

                    {/* Sort Selector */}
                    <div className="flex items-center gap-1.5 text-xs">
                        <span className="text-slate-400 dark:text-slate-400 font-medium">Sort:</span>
                        <select
                            value={selectedSort}
                            onChange={(e) => onSortChange(e.target.value)}
                            className="bg-slate-50 dark:bg-[#141414] border border-slate-200 dark:border-[#303030] rounded-lg text-xs font-semibold text-slate-700 dark:text-slate-200 py-1.5 pl-2.5 pr-7 focus:ring-2 focus:ring-blue-500 cursor-pointer shadow-2xs outline-none"
                        >
                            <option value="newest">Newest First</option>
                            <option value="oldest">Oldest First</option>
                            <option value="urgent">Priority (Urgent)</option>
                        </select>
                    </div>

                    {/* Mentions Scope Selector (Admin only on Mentions tab) */}
                    {activeTab === 'mentions' && isAdmin && (
                        <div data-tour="inbox-mentions-scope" className="flex items-center gap-1.5 text-xs">
                            <span className="text-purple-600 dark:text-purple-400 font-semibold">Mentions:</span>
                            <select
                                value={mentionsScope}
                                onChange={(e) => onMentionsScopeChange?.(e.target.value)}
                                className="bg-purple-50/80 dark:bg-purple-950/40 border border-purple-200 dark:border-purple-800/60 rounded-lg text-xs font-bold text-purple-800 dark:text-purple-300 py-1.5 pl-2.5 pr-7 focus:ring-2 focus:ring-purple-500 cursor-pointer shadow-2xs outline-none"
                            >
                                <option value="my">My Mentions</option>
                                <option value="all">All Team Mentions (Admin)</option>
                            </select>
                        </div>
                    )}
                </div>

                <div className="flex items-center gap-2.5 flex-shrink-0">
                    <div className="h-4 w-px bg-slate-200 dark:bg-[#303030] hidden sm:block" />
                    <button
                        type="button"
                        onClick={onRefresh}
                        disabled={loading}
                        aria-label="Refresh Inbox"
                        title="Refresh Inbox data"
                        className="w-8 h-8 text-slate-500 dark:text-slate-400 hover:text-[#03488A] dark:hover:text-[#00AAF1] hover:bg-slate-100 dark:hover:bg-[#252525] rounded-lg border border-slate-200 dark:border-[#303030] hover:border-slate-300 dark:hover:border-slate-600 transition-all active:scale-95 inline-flex items-center justify-center cursor-pointer disabled:opacity-60"
                    >
                        <span
                            className={`material-symbols-outlined text-[18px] inline-flex items-center justify-center select-none ${loading ? 'animate-spin text-[#03488A] dark:text-[#00AAF1]' : ''}`}
                            style={{ width: 18, height: 18, lineHeight: '18px', transformOrigin: 'center center' }}
                        >
                            refresh
                        </span>
                    </button>
                </div>
            </div>
        </div>
    );
}
